import React, { useState, useEffect, useCallback } from 'react';
import AppLoader from '../../shared/AppLoader';
import useAPI from '../../utils/api';
import DownloadIcon from '@mui/icons-material/Download';

function stripHtml(str) {
  return str ? str.replace(/<[^>]*>/g, '').trim() : str;
}

const DISPLAY_QUESTION_TYPES = [
  'short_text', 'long_text', 'number', 'email', 'phone', 'full_name',
  'dropdown', 'single_choice', 'multiple_choice', 'yes_no',
  'rating', 'rating_scale', 'address', 'date',
];

function formatAnswer(answer, question) {
  if (answer === null || answer === undefined || answer === '') return '—';
  if (Array.isArray(answer)) return answer.join(', ');
  if (question?.question_type === 'rating' || question?.question_type === 'rating_scale') {
    const max = question.scale_max || 5;
    return `${answer} / ${max}`;
  }
  return String(answer);
}

function exportToCsv(questions, responses) {
  const headers = ['Submitted At', ...questions.map(q => stripHtml(q.label))];
  const rows = responses.map(r => [
    r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '',
    ...questions.map(q => {
      const val = r.answers?.[q.label] ?? r.answers?.[stripHtml(q.label)] ?? r.answers?.[String(q.id)];
      if (val === null || val === undefined) return '';
      if (Array.isArray(val)) return val.join('; ');
      return String(val);
    }),
  ]);

  const csv = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'form-responses.csv';
  a.click();
  URL.revokeObjectURL(url);
}

const FORM_TYPE_LABELS = {
  survey:   'Survey',
  poll:     'Poll',
  lead_gen: 'Lead Generation',
  feedback: 'Customer Feedback',
  quiz:     'Quiz',
  contact:  'Contact Form',
  crm:      'CRM Inquiry',
};

export default function FormResponses({ form, onBack, siteId }) {
  const formId = form?.form_id;
  const API = useAPI();

  const formName = form?.name || form?.headline || 'Form';
  const formTypeLabel = FORM_TYPE_LABELS[form?.form_type] || form?.form_type || 'Form';
  const questions = (form?.form_questions || [])
    .filter(q => DISPLAY_QUESTION_TYPES.includes(q.question_type))
    .sort((a, b) => a.position - b.position);

  const PER_PAGE = 50;
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const fetchData = useCallback(async (pageNum) => {
    setLoading(true);
    try {
      const [respOk, respData] = await API.formResponses(formId, pageNum);
      if (respOk) {
        // API returns { responses: [...], total, page, per_page }
        const items = Array.isArray(respData) ? respData : (respData.responses || []);
        setResponses(items);
        if (respData.total !== undefined) setTotal(respData.total);
      }
    } finally {
      setLoading(false);
    }
  }, [formId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(page); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <AppLoader visible={true} />;

  return (
    <div style={{ backgroundColor: '#F2F1F1', minHeight: '100vh', padding: '0' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <button
              onClick={onBack}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '6px', color: '#ED5A29', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}
            >
              ← Back to Forms
            </button>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
              Form: {formTypeLabel} · {formName}
            </div>
            <h1 style={{ fontSize: '30px', fontWeight: '800', color: '#23262A', margin: 0, lineHeight: '1.15' }}>
              Responses
            </h1>
          </div>

          {responses.length > 0 && (
            <button
              onClick={() => {
                exportToCsv(questions, responses);
                try {
                  window.analytics?.track('Form Responses Exported', { form_id: formId, site_id: siteId });
                } catch (_) {}
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 20px',
                background: '#ED5A29',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
                marginTop: '28px',
                letterSpacing: '0.3px',
              }}
            >
              Export CSV
              <DownloadIcon style={{ fontSize: '16px' }} />
            </button>
          )}
        </div>

        {/* Empty state */}
        {responses.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '80px 40px',
            background: '#fff', borderRadius: '12px',
            border: '1px solid #E5E7EB',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '20px', color: '#23262A', fontWeight: '700' }}>
              No responses yet
            </h3>
            <p style={{ margin: 0, color: '#6B7280', fontSize: '15px' }}>
              Responses will appear here once visitors start submitting this form.
            </p>
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>

            {/* Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Submitted At</th>
                    {questions.map(q => (
                      <th key={q.id} style={thStyle}>{stripHtml(q.label)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {responses.map((r) => (
                    <tr key={r.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                      <td style={{ ...tdStyle, color: '#6B7280', whiteSpace: 'nowrap' }}>
                        {r.submitted_at
                          ? new Date(r.submitted_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '—'}
                      </td>
                      {questions.map(q => (
                        <td key={q.id} style={tdStyle}>
                          {formatAnswer(r.answers?.[q.label] ?? r.answers?.[stripHtml(q.label)] ?? r.answers?.[String(q.id)], q)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer / Pagination */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 20px', borderTop: '1px solid #F3F4F6',
            }}>
              <span style={{ fontSize: '13px', color: '#6B7280' }}>
                {total} response{total !== 1 ? 's' : ''} total
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={pagBtn(page === 1)}
                >
                  ‹ Previous
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    style={{
                      width: '32px', height: '32px',
                      borderRadius: '6px',
                      border: n === page ? 'none' : '1px solid #E5E7EB',
                      background: n === page ? '#ED5A29' : '#fff',
                      color: n === page ? '#fff' : '#23262A',
                      fontSize: '13px', fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    {n}
                  </button>
                ))}

                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages}
                  style={pagBtn(page >= totalPages)}
                >
                  Next ›
                </button>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

const thStyle = {
  padding: '13px 20px',
  textAlign: 'left',
  fontSize: '11px',
  fontWeight: '700',
  color: '#9CA3AF',
  textTransform: 'uppercase',
  letterSpacing: '0.7px',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #F3F4F6',
  background: '#FAFAFA',
};

const tdStyle = {
  padding: '16px 20px',
  color: '#23262A',
  maxWidth: '280px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '14px',
};

const pagBtn = (disabled) => ({
  padding: '6px 12px',
  fontSize: '13px',
  fontWeight: '600',
  borderRadius: '6px',
  border: '1px solid #E5E7EB',
  background: '#fff',
  color: disabled ? '#D1D5DB' : '#23262A',
  cursor: disabled ? 'default' : 'pointer',
});
